import { globalUser } from "@/contexts/UserContext";
import { LocalStorageKeys } from "@/utils/localStorageUtils";
import { AuthSignupResponse, AuthSignupBody, AuthLoginBody, AuthLoginResponse, AuthForgotPasswordBody, AuthResetPasswordBody, BxError, UserRole, PollingResponse, ErrorCode, ErrorSource } from "@boxem/core-api-schema";

// these functions just wrap the browser's fetch api, and assume a few things:
//  - you want to hit our API as defined by PUBLIC_API_URL
//  - you want to send credentials
//  - if you provide a body in your request, it will be type 'application/json'
//  - all responses will be empty or valid JSON
//  - a response code of 401 means we should log-out
//  - any non-ok (200/100/300-series) status means we should reject the promise
//  - any 500-series status means we should open a generic error modal
//
// if any of the above isn't true, just call the fetch api yourself manually and do it.
// we do this in 'PrinterContext.tsx'

export function parseJwt(token) {
  var base64Url = token.split(".")[1];
  var base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
  var jsonPayload = decodeURIComponent(
    window
      .atob(base64)
      .split("")
      .map(function (c) {
        return "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2);
      })
      .join("")
  );

  return JSON.parse(jsonPayload);
}

const pollingLog = <T>(method: string, url: string, iterationCount: number, start: number, response: PollingResponse<T>, generatorYieldedValuesCount?: number) => {
  let yieldedMessage = `${generatorYieldedValuesCount ? "\n# of times we yielded values so far: " + generatorYieldedValuesCount + "\n" : ""}`;
  let elapsedTimeSuffix = `${generatorYieldedValuesCount ? "(this is a generator function and may yield more values, we will reset the performance counter here)" : ""}`;
  console.info(`Operation: ${method}, ${url}
Iteration count: ${iterationCount}${yieldedMessage}
Total elapsed time: ${performance.now() - start}ms ${elapsedTimeSuffix}
While polling, got response: `, response);
}

/**
 * Initializes an in-memory access token within a closure that is used to make authenticated API calls.
 */
export const API = (() => {
  const prefix = `${process.env.PUBLIC_API_URL}`;

  let accessToken = "";

  // If a refresh is currently in progress, it will be stored here so that subsequent requests can wait for it to complete.
  let refreshPromise: Promise<string> | null = null;

  /**
   * Either returns the current access token if not expired/missing, or generates a new one
   * by calling /auth/refresh.
   */
  const getAccessToken = async () => {
    if (refreshPromise != null) {
      return refreshPromise;
    }

    let needNewToken = true;

    if (accessToken) {
      try {
        const tokenData = parseJwt(accessToken);
        const tokenExpirationDate = new Date(0);
        tokenExpirationDate.setUTCSeconds(tokenData.exp);
        if (tokenExpirationDate > new Date()) {
          needNewToken = false;
          // console.log("Access token is fresh!");
        }
      } catch (err) {
        needNewToken = true;
      }
    }

    if (needNewToken) {
      // console.log("getting access token...");
      refreshPromise = fetch(`${process.env.PUBLIC_API_URL}/auth/refresh`, {
        method: "POST",
        credentials: "include",
      })
        .then((res) => res.json())
        .then((refreshData) => {
          accessToken = refreshData.accessToken;
          return accessToken;
        })
        .catch(errors => {
          if (!window.location.pathname.startsWith("/auth")) {
            window.location.href = "/auth/login";
          }
          return "";
        })
        .finally(() => {
          refreshPromise = null;
        });

      return refreshPromise;
    }

    return accessToken;
  };

  function handleResponse(response: Response) {
    if (response.status === 401) {
      // if we get a 401, we should log you out.
      if (!window.location.pathname.startsWith("/auth")) {
        window.location.href = `/auth/login?path=${encodeURIComponent(window.location.pathname)}${encodeURIComponent(window.location.search)}`;
      }
      return response.json().then((json) => {
        throw json;
      });
    } else if (!response.ok) {
      // we have a 400/500 series status code (non-401). we should have an array of errors on the body
      // that call-sites should handle as they see fit.
      // note that this implicitly calls the 'reject' function of the parent promise.
      // You don't have to use a try-catch block in your calling code (though you can if you want).
      return response.json()
        .catch(() => ({ errors: [{ details: `status: ${response.status}`}] }))
        .then(json => { throw json });
    }

    // sometimes we don't send a body back! in which case, calling `response.json()` throws a SyntaxError.
    // only try to parse json or blob if we see a body...
    // we still throw if we get back say, a text/html response, which I think is correct, we want to see that when it happens
    const contentLength = response.headers.get("content-length");
    const contentType = response.headers.get("content-type");
    if (contentLength && +contentLength > 0) {
      if (contentType && contentType.includes("application/octet-stream")) {
        return response.blob();
      } else {
        return response.json();
      }
    }
  };

  const handleSeller = (params?: URLSearchParams) => {
    if (globalUser.user?.role === UserRole.SELLER) {
      return params;
    }

    if (globalUser.seller) {
      const allParams = params ? params : new URLSearchParams();
      allParams.append('ownerUUID', globalUser.seller.uuid);
      return allParams;
    }
    return params;
  }

  // makes some request using our API repeatedly until it fails or the response has 'keepPolling: false'.
  // when we get a failure or a '!keepPolling', then we'll resolve with a 'Promise<PollingResponse<T>>'.
  async function pollingRequest<T>(
    method: "GET" | "POST" | "PUT" | "PATCH" | "DELETE",
    url: string,
    body?: Object,
    params?: URLSearchParams,
    pollingIntervalMilliseconds: number = 2000,
    abortPollingOnNavigate: boolean = false,

    // if the response body on 'data' has this property on it, we will set that property
    // to the request body on subsequent requests we make. this is used for certain polling
    // operations that return us an 'id' of some sort to identify the originating request.
    copyPropertyToSubsequentRequests?: keyof PollingResponse<T>["data"],

    // only make a maximum of this # of requests (or infinite if === 0)
    abortPollingAfterCount: number = 100,

  ): Promise<PollingResponse<T>> {
    const start = performance.now();
    const allParams = handleSeller(params);
    const fullUrl = `${prefix}${url}${allParams ? "?" + allParams.toString() : ""}`;

    const headers = {
      Authorization: await getAccessToken(),
    };
    let stringifiedBody;
    if (body) {
      stringifiedBody = JSON.stringify(body);
      headers["Content-Type"] = "application/json";
    }

    // url fragment after the TLD/ not including query params or hashcode
    // we check when polling if this has changed to determine if we should
    // abort polling because the user navigated away.
    const originalLocation = window.location.pathname;

    return new Promise((resolve, _) => {
      const pollingState = { intervalId: null, iterationCount: 1, promise: null };

      const pollingCall = async () => {

        if (abortPollingOnNavigate && originalLocation !== window.location.pathname) {
          console.info("Aborting polling because a user navigation occcured.");
          clearInterval(pollingState.intervalId);
          resolve({
            keepPolling: false,
            errors: [{ type: ErrorCode.POLLING_WAS_ABORTED_BECAUSE_OF_USER_NAVIGATE }],
          });
          return;
        }

        if (abortPollingAfterCount > 0 && pollingState.iterationCount > abortPollingAfterCount) {
          console.info(`Aborting polling because we exceeded ${abortPollingAfterCount} iterations.`);
          clearInterval(pollingState.intervalId);
          resolve({
            keepPolling: false,
            errors: [{ type: ErrorCode.POLLING_WAS_ABORTED_BECAUSE_OF_EXCEEDING_MAX_ITERATIONS }],
          });
          return;
        }

        if (pollingState.promise) return; // there's a request in progress for this poll that hasn't ended yet

        try {
          pollingState.promise = fetch(fullUrl, {
            method,
            body: stringifiedBody,
            headers,
            credentials: "include",
          }).then(handleResponse);

          const response: PollingResponse<T> = await pollingState.promise;
          pollingState.promise = null;

          if (!response.keepPolling) {
            pollingLog(method, fullUrl, pollingState.iterationCount, start, response);
            clearInterval(pollingState.intervalId);
            resolve(response);
            return;
          }

          if (copyPropertyToSubsequentRequests && !!response.data?.[copyPropertyToSubsequentRequests]) {
            if (!body) body = {}; // sometimes the body is initially null.
            (<Partial<PollingResponse<T>["data"]>>body)[copyPropertyToSubsequentRequests] = response.data[copyPropertyToSubsequentRequests];
            stringifiedBody = JSON.stringify({ ...body });
          }

          pollingState.iterationCount += 1;

        } catch (e: any) {
          console.error("Exception thrown when making polling request: ", e);        
          clearInterval(pollingState.intervalId);
          resolve(e);    
        }
      };

      pollingState.intervalId = setInterval(pollingCall, pollingIntervalMilliseconds);
      pollingCall(); // don't wait the polling interval for the first call.
    });
  };

  // exactly the same as above but it is a generator function, so you can get multiple return values over time
  async function* pollingRequestMultiple<T>(
    method: "GET" | "POST" | "PUT" | "PATCH" | "DELETE",
    url: string,
    body?: Object,
    params?: URLSearchParams,
    pollingIntervalMilliseconds: number = 2000,
    abortPollingOnNavigate: boolean = true, // defaulted true here, because it's easier to make a mistake with the generator

    // if the response body on 'data' has this property on it, we will set that property
    // to the request body on subsequent requests we make. this is used for certain polling
    // operations that return us an 'id' of some sort to identify the originating request.
    copyPropertyToSubsequentRequests?: keyof PollingResponse<T>["data"],

    // only make a maximum of this # of requests (or infinite if === 0)
    abortPollingAfterCount: number = 0,

  ): AsyncGenerator<PollingResponse<T>, never, void> {
    let start = performance.now();
    const allParams = handleSeller(params);
    const fullUrl = `${prefix}${url}${allParams ? "?" + allParams.toString() : ""}`;

    const headers = {
      Authorization: await getAccessToken(),
    };
    let stringifiedBody;
    if (body) {
      stringifiedBody = JSON.stringify(body);
      headers["Content-Type"] = "application/json";
    }

    // url fragment after the TLD/ not including query params or hashcode
    // we check when polling if this has changed to determine if we should
    // abort polling because the user navigated away.
    const originalLocation = window.location.pathname;

    const pollingState = {
      iterationCount: 1,
      promise: null,
      numTimesYieldedValues: 0, // number of times we've yielded (minus a couple error cases)
    };

    const logYield = (response: PollingResponse<T>) => {
      pollingLog(method, fullUrl, pollingState.iterationCount, start, response, pollingState.numTimesYieldedValues);
      start = performance.now();
    };
  
    do {
      if (abortPollingOnNavigate && originalLocation !== window.location.pathname) {
        console.info("Aborting polling because a user navigation occcured.");
        yield new Promise(resolve => resolve({
          keepPolling: false,
          errors: [{ type: ErrorCode.POLLING_WAS_ABORTED_BECAUSE_OF_USER_NAVIGATE }],
        }));
        return;
      }

      if (abortPollingAfterCount > 0 && pollingState.iterationCount > abortPollingAfterCount) {
        console.info(`Aborting polling because we exceeded ${abortPollingAfterCount} iterations.`);
        yield new Promise(resolve => resolve({
          keepPolling: false,
          errors: [{ type: ErrorCode.POLLING_WAS_ABORTED_BECAUSE_OF_EXCEEDING_MAX_ITERATIONS }],
        }));
        return;
      }

      if (pollingState.promise) continue; // there's a request in progress for this poll that hasn't ended yet

      try {
        pollingState.promise = fetch(fullUrl, {
          method,
          body: stringifiedBody,
          headers,
          credentials: "include",
        }).then(handleResponse);

        const response: PollingResponse<T> = await pollingState.promise;
        pollingState.promise = null;

        if (!response.keepPolling) {
          pollingState.numTimesYieldedValues += 1;
          logYield(response);
          yield new Promise(resolve => resolve(response));
          return; // we're done.
        }

        if (response.data || response.errors) {
          pollingState.numTimesYieldedValues += 1;
          logYield(response);
          yield new Promise(resolve => resolve(response));
        }

        if (copyPropertyToSubsequentRequests && !!response.data?.[copyPropertyToSubsequentRequests]) {
          if (!body) body = {}; // sometimes the body is initially null.
          (<Partial<PollingResponse<T>["data"]>>body)[copyPropertyToSubsequentRequests] = response.data[copyPropertyToSubsequentRequests];
          stringifiedBody = JSON.stringify({ ...body });
        }

        pollingState.iterationCount += 1;

      } catch (e: any) {
        console.error("Exception thrown when making polling request: ", e);        
        yield new Promise(resolve => resolve(e));
        return;
      }

      // wait the interval
      await new Promise<void>(resolve => setTimeout(() => resolve(), pollingIntervalMilliseconds));

    } while (true);
  };

  return {
    prefix,
    getAccessToken,
    handleSeller,

    pollingRequest,
    pollingRequestMultiple,

    async get<T>(url: string, params?: URLSearchParams): Promise<T> {
      const allParams = handleSeller(params);
      return fetch(`${prefix}${url}${allParams ? "?" + allParams.toString() : ""}`, {
        headers: {
          Authorization: await getAccessToken(),
        },
        credentials: "include",
      }).then(handleResponse);
    },

    async post<T>(url: string, body?: Object, params?: URLSearchParams): Promise<T> {
      const headers = {
        Authorization: await getAccessToken(),
      };
      if (body) {
        headers["Content-Type"] = "application/json";
      }

      const allParams = handleSeller(params);
      return fetch(`${prefix}${url}${allParams ? "?" + allParams.toString() : ""}`, {
        method: "POST",
        body: body && JSON.stringify(body),
        headers,
        credentials: "include",
      }).then(handleResponse);
    },

    async patch<T>(url: string, body?: Object, params?: URLSearchParams, keepalive: boolean = false): Promise<T> {
      const headers = {
        Authorization: await getAccessToken(),
      };
      if (body) {
        headers["Content-Type"] = "application/json";
      }
      const allParams = handleSeller(params);
      const urlToUse = `${prefix}${url}${allParams ? "?" + allParams.toString() : ""}`;
      return fetch(urlToUse, {
        method: "PATCH",
        body: body && JSON.stringify(body),
        headers,
        credentials: "include",
        keepalive,
      }).then(handleResponse).catch((err) => { console.error("Failed to fetch '" + urlToUse + "'", err); } );
    },

    async delete<T>(url: string, body?: Object, params?: URLSearchParams): Promise<T> {
      const headers = {
        Authorization: await getAccessToken(),
      };
      if (body) {
        headers["Content-Type"] = "application/json";
      }
      const allParams = handleSeller(params);
      return fetch(`${prefix}${url}${allParams ? "?" + allParams.toString() : ""}`, {
        method: "DELETE",
        body: body && JSON.stringify(body),
        headers,
        credentials: "include",
      }).then(handleResponse);
    },

    // Login, Signup, Logout, and Refresh are specially included here, because they modify the access token.
    // Other API endpoint helpers need not be in this object.

    async login(payload: AuthLoginBody): Promise<[AuthLoginResponse["user"], BxError]> {
      const res = await fetch(`${process.env.PUBLIC_API_URL}/auth/login`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
        credentials: "include",
      });
      const data = await res.json();

      if (res.ok) {
        accessToken = data.accessToken;

        return [data.user, null];
      } else {
        return [null, { source: ErrorSource.BOXEM, message: data.message }];
      }
    },

    async loginAsUser(mirrorToken: string): Promise<[AuthLoginResponse["user"], BxError]> {
      const res = await fetch(`${process.env.PUBLIC_API_URL}/auth/login-as-user`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ mirrorToken }),
        credentials: "include",
      });
      const data = await res.json();

      if (res.ok) {
        accessToken = data.accessToken;

        return [data.user, null];
      } else {
        return [null, { source: ErrorSource.BOXEM, message: data.message }];
      }
    },

    async signup(payload: AuthSignupBody) {
      const res: AuthSignupResponse = await fetch(`${process.env.PUBLIC_API_URL}/auth/signup`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
        credentials: "include",
      }).then((res) => {
        if (!res.ok) {
          return res.json().then(json => { throw json; });
        }
        return res.json();
      });
      accessToken = res.accessToken;
      return res.user;
    },

    async logout() {
      try {
        window.Intercom?.("shutdown");
      } catch (err) { }
      accessToken = "";
      return API.post("/auth/logout", null, null).catch((e) => { });
    },

    async refresh() {
      const refreshData = await fetch(`${process.env.PUBLIC_API_URL}/auth/refresh`, {
        method: "POST",
        credentials: "include",
      }).then((res) => res.json());
      accessToken = refreshData.accessToken;
    },

    forgotPassword(payload: AuthForgotPasswordBody) {
      return fetch(`${process.env.PUBLIC_API_URL}/auth/forgot-password`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
        credentials: "include",
      })
        .then(res => {
          if (!res.ok) {
            throw new Error();
          }
          return;
        });
    },

    resetPassword(payload: AuthResetPasswordBody) {
      return fetch(`${process.env.PUBLIC_API_URL}/auth/reset-password`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
        credentials: "include",
      })
        .then(res => {
          if (!res.ok) {
            throw new Error();
          }
          return;
        });
    }
  };
})();  

export const API2 = (() => {
  const prefix = `${process.env.PUBLIC_API2_URL}`;

  const handleResponse = (response: Response) => {
    if (response.status === 401) {
      localStorage.removeItem(LocalStorageKeys.user);
      localStorage.removeItem(LocalStorageKeys.seller);
      if (!window.location.pathname.startsWith("/auth")) {
        window.location.href = `/auth/login?path=${encodeURIComponent(window.location.pathname)}${encodeURIComponent(window.location.search)}`;
      }
      return response.json().then((json) => {
        throw json;
      });
    } else if (!response.ok) {
      return response.json()
        .catch(() => ({ errors: [{ details: `status: ${response.status}`}] }))
        .then(json => { throw json });
    }

    const contentType = response.headers.get("content-type");
    try {
      if (contentType && contentType.includes("application/octet-stream")) {
        return response.blob();
      } else {
        return response.json();
      }
    } catch(err) {}
  };

  // Create a new instance with the same methods but different prefix
  const api2 = {
    prefix,
    getAccessToken: API.getAccessToken,
    handleSeller: API.handleSeller,
    pollingRequest: API.pollingRequest,
    pollingRequestMultiple: API.pollingRequestMultiple,
    
    async get<T>(url: string, params?: URLSearchParams): Promise<T> {
      const allParams = this.handleSeller(params);
      return fetch(`${prefix}${url}${allParams ? "?" + allParams.toString() : ""}`, {
        headers: {
          Authorization: await this.getAccessToken(),
        },
        credentials: "include",
      }).then(handleResponse);
    },

    async post<T>(url: string, body?: Object, params?: URLSearchParams): Promise<T> {
      const headers = {
        Authorization: await this.getAccessToken(),
      };
      if (body) {
        headers["Content-Type"] = "application/json";
      }

      const allParams = this.handleSeller(params);
      const urlToUse = `${prefix}${url}${allParams ? "?" + allParams.toString() : ""}`;
      return fetch(urlToUse, {
        method: "POST",
        body: body && JSON.stringify(body),
        headers,
        credentials: "include",
      }).then(handleResponse).catch((err) => {
        console.error("Error on fetching '" + urlToUse + "'", err);
      });
    },

    async patch<T>(url: string, body?: Object, params?: URLSearchParams, keepalive: boolean = false): Promise<T> {
      const headers = {
        Authorization: await this.getAccessToken(),
      };
      if (body) {
        headers["Content-Type"] = "application/json";
      }
      const allParams = this.handleSeller(params);
      return fetch(`${prefix}${url}${allParams ? "?" + allParams.toString() : ""}`, {
        method: "PATCH",
        body: body && JSON.stringify(body),
        headers,
        credentials: "include",
        keepalive,
      }).then(handleResponse);
    },

    async delete<T>(url: string, body?: Object, params?: URLSearchParams): Promise<T> {
      const headers = {
        Authorization: await this.getAccessToken(),
      };
      if (body) {
        headers["Content-Type"] = "application/json";
      }
      const allParams = this.handleSeller(params);
      return fetch(`${prefix}${url}${allParams ? "?" + allParams.toString() : ""}`, {
        method: "DELETE",
        body: body && JSON.stringify(body),
        headers,
        credentials: "include",
      }).then(handleResponse);
    },

    login: API.login,
    loginAsUser: API.loginAsUser,
    signup: API.signup,
    logout: API.logout,
    refresh: API.refresh,
    forgotPassword: API.forgotPassword,
    resetPassword: API.resetPassword
  };

  return api2;
})();